import React, { useContext } from 'react';
import { IAuctionItem } from 'src/pages/CatalogPage/CatalogPage.i';
import { LoginContext } from 'src/lib/LoginContext';
import { BidModal } from 'src/components/PlaceBid/PlaceBidModal';
import { TAuctionTypeID } from 'src/pages/CatalogPage/Catalog.i';

import { useOverlayTriggerState } from '@react-stately/overlays';
import { Button, cn, IconMail, IconMaxBid, IconPhone } from '@riac/design-system';
import { BiddingContext, NotEnabledMessage } from 'src/lib/BiddingContext';

export type TBidButtonPage = 'catalog' | 'detail' | 'cart' | 'my-bids';
interface IPlaceBid {
  type: 'sealed' | 'phone' | 'increment';
  item: IAuctionItem;
  auctionTypeID: TAuctionTypeID;
  addBid(bid?: number, bidPlus?: number): void;
  className?: string;
  button?: React.FC;
  variant?: 'secondary';
  size?: 'sm' | 'md' | 'lg';
}

export const PlaceBid = (props: IPlaceBid):JSX.Element => {
  const bidModalOverlayState = useOverlayTriggerState({});
  const { isLoggedIn } = useContext(LoginContext);
  const { placeTimedAuctionBid, checkEnabled, setMessageForLot } = useContext(BiddingContext);
  const { auctionTypeID, className } = props;

  async function toggleSealedBidModal():Promise<void> {
    if(!isLoggedIn) return;
    if(auctionTypeID === TIMED_AUCTION_TYPE_ID && !await checkEnabled()) {
      setMessageForLot(props.item.LotNumber, 'error', NotEnabledMessage);
      return;
    }
    bidModalOverlayState.open();
  }
  async function bidNext():Promise<void> {
    if(!isLoggedIn || auctionTypeID !== TIMED_AUCTION_TYPE_ID) return;
    const placed = await placeTimedAuctionBid(props.item, () => {});
    if(!placed.success) {
      return;
    }
    if(typeof props.addBid === 'function') {
      props.addBid();
    }
  }
  // No bid text on catalog page
  const bidTextUncapitalized = auctionTypeID === TIMED_AUCTION_TYPE_ID
    ? (props.type === 'increment' ? 'Next' : 'Max')
    : props.type === 'sealed' ? 'Place' : props.type;
  // capitalize first letter of bid text
  const bidText = bidTextUncapitalized.charAt(0).toUpperCase() + bidTextUncapitalized.slice(1);

  const onPress = async () => {
    if(props.item.MyItem) {
      setMessageForLot(props.item.LotNumber, 'error', 'As the Consignor you cannot bid on your own lot.');
      return;
    }

    if(props.type === 'increment') {
      await bidNext();
    } else {
      await toggleSealedBidModal();
    }
  };

  const InjectedButton = props.button;

  return (
    <>
      {InjectedButton
        ? (
          <InjectedButton
            excludeFromTabOrder={!isLoggedIn}
            onPress={onPress}
            className={cn('capitalize flex flex-row gap-2 items-center justify-center', className)}
          />
        )
        : (
          <Button
            excludeFromTabOrder={!isLoggedIn}
            onPress={onPress}
            className={cn('flex flex-row gap-2 items-center justify-center', className)}
            variant={props.variant}
            size={props.size || 'sm'}
          >
            <div className='block md:hidden xl:block'>
              {props.type === 'sealed' ? <IconMail/> : null}
              {props.type === 'increment' ? <IconMaxBid/> : null}
              {props.type === 'phone' ? <IconPhone/> : null}
            </div>
            {bidText} Bid
          </Button>
        )
      }
      {bidModalOverlayState.isOpen && (
        <BidModal
          item={props.item}
          auctionTypeId={props.auctionTypeID}
          type={props.type}
          closeModal={() => bidModalOverlayState.close()}
          bidUpdatedCallback={props.addBid}
        />
      )}
    </>
  );};
